import type { ReactNode } from 'react'
import { ExternalLink, Lock } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import type {
  RegulatoryApplicability,
  RegulatoryFrameworkDetailBrief,
  RegulatoryFrameworkDetailFramework,
  RegulatoryKeyAnnexRef,
  RegulatoryKeyObligation,
  RegulatoryPenaltyStructure,
} from '@/hooks/useRegulatoryQueries'

const EMPTY_FALLBACK = 'Not specified'

/**
 * KAN-271 s2 — the framework detail "definition" header. Renders the
 * regulation's identity (lock + name + legal citation + type badge), the
 * scope summary, a key-facts field grid, key obligations, and related-framework
 * cross-links. Deliberately carries NO footer meta line (the "questions ·
 * domains" footer was dropped in the redesign). Every field degrades to a
 * graceful fallback when its source value is null/empty.
 *
 * Mounted only on the `'ready'` arm of the detail page, so `brief` is always
 * present.
 */
export function FrameworkDetailHeader({
  framework,
  brief,
}: {
  framework: RegulatoryFrameworkDetailFramework
  brief: RegulatoryFrameworkDetailBrief
}) {
  const applies = applicabilityChips(brief.applicability)
  const annexes = (brief.keyAnnexesRefs ?? []).filter((a) => a && (a.ref || a.label))
  const obligations = brief.keyObligations ?? []
  const related = crosswalkChips(brief.crosswalk)
  const possibleFines = penaltyHeadline(brief.penaltyStructure)

  return (
    <header
      data-testid="framework-detail-header"
      className="rounded-lg border bg-card p-6 shadow-sm"
      aria-label="Framework overview"
    >
      <div className="flex max-w-3xl flex-col gap-3">
        <div className="flex items-center gap-2">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
            aria-hidden
          >
            <Lock className="h-4 w-4" />
          </span>
          <h1 className="text-3xl font-semibold leading-tight">{framework.name}</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {framework.regCitation ? (
            <span className="text-sm text-muted-foreground">{framework.regCitation}</span>
          ) : null}
          {framework.frameworkType ? (
            <Badge variant="outline" className="capitalize">
              {framework.frameworkType}
            </Badge>
          ) : null}
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground">
          {brief.scopeSummary?.trim() || 'No scope summary available yet.'}
        </p>
      </div>

      <dl className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Applies to">
          {applies.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {applies.map((value) => (
                <Badge key={value} variant="secondary" className="font-normal">
                  {value}
                </Badge>
              ))}
            </div>
          ) : (
            <EmptyValue />
          )}
        </Field>

        <Field label="In force / applies">
          <ValueText value={brief.inForceDate ? formatDate(brief.inForceDate) : null} />
        </Field>

        <Field label="Possible fines">
          <ValueText value={possibleFines} />
        </Field>

        <Field label="Key annexes / refs">
          {annexes.length > 0 ? (
            <ul className="space-y-1">
              {annexes.map((annex) => (
                <li key={`${annex.ref}-${annex.label}`} className="text-sm">
                  {annex.ref ? <span className="font-medium">{annex.ref}</span> : null}
                  {annex.ref && annex.label ? ' — ' : null}
                  {annex.label ? (
                    <span className="text-muted-foreground">{annex.label}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyValue />
          )}
        </Field>

        <Field label="Supervisory authority">
          <ValueText value={emptyToNull(brief.supervisoryAuthority)} />
        </Field>

        <Field label="Geographic scope">
          <ValueText value={emptyToNull(brief.geographicScope)} />
        </Field>
      </dl>

      <section aria-labelledby="header-obligations-heading" className="mt-6 space-y-2">
        <h2 id="header-obligations-heading" className="text-sm font-semibold">
          Key obligations
        </h2>
        {obligations.length > 0 ? (
          <ul className="list-disc space-y-1.5 pl-5 text-sm">
            {obligations.map((obligation) => (
              <li key={obligation.title}>
                <span className="font-medium">{obligation.title}</span>
                {obligation.description ? (
                  <span className="text-muted-foreground"> — {obligation.description}</span>
                ) : null}
                {obligation.citation_url ? (
                  <a
                    href={obligation.citation_url}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Citation
                    <ExternalLink className="h-3 w-3" aria-hidden />
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No obligations published yet.</p>
        )}
      </section>

      <section aria-labelledby="header-related-heading" className="mt-6 space-y-2">
        <h2 id="header-related-heading" className="text-sm font-semibold">
          Related frameworks
        </h2>
        {related.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {related.map((value) => (
              <Badge key={value} variant="outline" className="font-normal">
                {value}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No related frameworks linked.</p>
        )}
      </section>
    </header>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-2 text-sm">{children}</dd>
    </div>
  )
}

function ValueText({ value }: { value: string | null | undefined }) {
  if (!value) return <EmptyValue />
  return <span>{value}</span>
}

function EmptyValue() {
  return <span className="text-muted-foreground">{EMPTY_FALLBACK}</span>
}

function emptyToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/** Headline penalty line: prefer the formula text, fall back to the max fine. */
function penaltyHeadline(structure: RegulatoryPenaltyStructure | null | undefined): string | null {
  if (!structure) return null
  const formula = structure.formula_text?.trim()
  if (formula) return formula
  return formatEuro(structure.max_fine_eur)
}

/** Flatten the applicability dimensions into a deduped chip list. */
function applicabilityChips(applicability: RegulatoryApplicability | null | undefined): string[] {
  if (!applicability) return []
  const out: string[] = []
  for (const value of Object.values(applicability)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        const text = String(item).trim()
        if (text && !out.includes(text)) out.push(text)
      }
    } else if (value !== null && value !== undefined) {
      const text = String(value).trim()
      if (text && !out.includes(text)) out.push(text)
    }
  }
  return out
}

/** Flatten crosswalk values (control families, topic tags, …) into chips. */
function crosswalkChips(crosswalk: Record<string, unknown> | null | undefined): string[] {
  if (!crosswalk) return []
  const out: string[] = []
  for (const value of Object.values(crosswalk)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        const text = String(item).trim()
        if (text && !out.includes(text)) out.push(text)
      }
    } else if (typeof value === 'string') {
      const text = value.trim()
      if (text && !out.includes(text)) out.push(text)
    }
  }
  return out
}

function formatEuro(value: string | null | undefined): string | null {
  if (!value) return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return value
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(numeric)
}

// Re-export the obligation type for co-located tests / consumers.
export type { RegulatoryKeyAnnexRef, RegulatoryKeyObligation }
